import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setPlayers,
  addPlayer as addStorePlayer,
  removePlayer as removeStorePlayer,
} from "../../features/coachPlayers/coachPlayersSlice";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import { API_BASE } from "../../config/apiConfig";
import { FaUserCircle, FaUserPlus, FaUserMinus } from "react-icons/fa";

export default function ManagePlayers() {
  const token = sessionStorage.getItem("access_token");
  const dispatch = useDispatch();
  const myPlayers = useSelector((state) => state.coachPlayers.players);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [loading, setLoading] = useState(false);

  // ----------------------------------------------------------------------
  // 1. Load MY PLAYERS when page loads
  // ----------------------------------------------------------------------
  useEffect(() => {
    fetchMyPlayers();
  }, []);

  useEffect(() => {
    if (search.trim() === "") {
      setHasSearched(false);
      setSearchResults([]);
    }
  }, [search]);

  async function fetchMyPlayers() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/coach-players-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const sorted = data.players.sort((a, b) =>
          a.fullName.localeCompare(b.fullName)
        );
        dispatch(setPlayers(sorted));
      }
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------------------------------------------------
  // 2. Search players (only unassigned players)
  // ----------------------------------------------------------------------
  async function handleSearch() {
    const q = search.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/player-search?query=${encodeURIComponent(q)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (data.success) setSearchResults(data.results);
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------------------------------------------------
  // 3. ADD / REMOVE Player
  // ----------------------------------------------------------------------
  async function addPlayer(p) {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/coach-add-player`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: p.user_id }),
      });

      const data = await res.json();

      if (data.success) {
        dispatch(addStorePlayer(p));
        setSearchResults([]);
        setSearch("");
      }
    } catch (err) {
      console.error("Add player error:", err);
    } finally {
      setLoading(false);
    }
  }

  async function removePlayer(p) {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/coach-remove-player`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: p.user_id }),
      });
      
      const data = await res.json();

      if (data.success) {
        dispatch(removeStorePlayer(p));
      }
    } catch (err) {
      console.error("Remove player error:", err);
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------------------------------------------------
  // RENDER PLAYER ROW
  // ----------------------------------------------------------------------
  const PlayerRow = ({ p, actionButton, isMyPlayer }) => (
    <div className="flex items-center justify-between p-3 border rounded-lg bg-white shadow-sm">
      <div className="flex items-center gap-3">
        {p.photo ? (
          <img src={p.photo} className="w-10 h-10 rounded-full object-cover border" />
        ) : (
          <FaUserCircle className="w-10 h-10 text-gray-400" />
        )}
        <span className="font-medium">{p.fullName}</span>
      </div>

      <button onClick={actionButton} className="text-xl">
        {isMyPlayer ? (
          <FaUserMinus className="text-red-500" />
        ) : (
          <FaUserPlus className="text-green-600" />
        )}
      </button>
    </div>
  );


  // ----------------------------------------------------------------------
  // MAIN RENDER
  // ----------------------------------------------------------------------
  const canSearch = search.trim().length >= 2;

  return (
    <MainContentLayout>
      <PageHeader title="Manage Players" backTo="/dashboard" />
      <LoadingOverlay show={loading} />

      <div className="max-w-xl mx-auto w-full space-y-6">

        {/* Search Bar */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search players by name/email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-grow p-3 border rounded-lg placeholder:italic placeholder:text-gray-400"
          />

          <button
            onClick={handleSearch}
            disabled={!canSearch}
            className={`px-4 rounded-lg  ${canSearch ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
          >
            Search
          </button>
        </div>

        {/* If searching */}
        {hasSearched && searchResults.length > 0 && (
          <div className="space-y-3">
            <h1 className="mt-2">Search Results</h1>

            {searchResults.map((p) => (
              <PlayerRow
                key={p.user_id}
                p={p}
                isMyPlayer={false}
                actionButton={() => addPlayer(p)}
              />
            ))}
          </div>
        )}

        {/* Show NO results */}
        {hasSearched && !loading && searchResults.length === 0 && (
          <p className="text-gray-500">No matching players.</p>
        )}

        {/* If NOT searching → show My Players */}
        {!hasSearched && (
          <>
            <h1>My Players ({myPlayers.length})</h1>

            {myPlayers.length === 0 && (
              <p className="text-gray-500">No players assigned yet.</p>
            )}

            <div className="space-y-3">
              {myPlayers.map((p) => (
                <PlayerRow
                  key={p.user_id}
                  p={p}
                  isMyPlayer={true}
                  actionButton={() => removePlayer(p)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </MainContentLayout>
  );
}
